import { SdkContext } from '../internal'
import { queuedExportOf, type QueuedExport } from '../fileDownload'
import type {
  AdminSubscriptionDetail,
  AdminSubscriptionFilters,
  AdminSubscriptionPage,
  CreateManualSubscriptionInput
} from '@/types/subscriptionAdmin'

/* F-45, the team's view. Not /subscriptions/{id}: that is the member's own. */
const BASE = '/manage/subscriptions'

/** Drops empty filters so the query string carries only what was chosen. */
const cleanQuery = (params: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/**
 * F-45: the team manages the community's subscriptions (owner, admin and
 * finance; every action is audited by the API). Each action answers the
 * subscription's detail after it.
 */
export class SubscriptionAdminResource {
  constructor(private ctx: SdkContext) {}

  async list(params: AdminSubscriptionFilters & { page?: number; limit?: number } = {}): Promise<AdminSubscriptionPage> {
    const result = await this.ctx.raw<
      AdminSubscriptionPage['items'] | (Partial<AdminSubscriptionPage> & { data?: AdminSubscriptionPage['items'] })
    >({ httpMethod: 'GET', path: BASE }, { query: cleanQuery({ ...params }) })
    if (Array.isArray(result)) {
      return { items: result, pagination: { page: 1, total: result.length, totalPages: 1 } }
    }
    const items = result?.items || result?.data || []
    return { items, pagination: result?.pagination || { page: 1, total: items.length, totalPages: 1 } }
  }

  /** The CSV as text, same filters as the list. Above 2,000 rows the file is e-mailed (202, `QueuedExport`). */
  async exportCsv(params: AdminSubscriptionFilters = {}): Promise<string | QueuedExport> {
    const result = await this.ctx.raw<unknown>(
      { httpMethod: 'GET', path: `${BASE}/export.csv` },
      { query: cleanQuery({ ...params }) }
    )
    if (typeof result === 'string') return result
    return queuedExportOf(result) || String(result ?? '')
  }

  async get(subscriptionId: string): Promise<AdminSubscriptionDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: `${BASE}/{subscriptionId}` }, { subscriptionId })
  }

  async create(body: CreateManualSubscriptionInput): Promise<AdminSubscriptionDetail> {
    return this.ctx.call({ httpMethod: 'POST', path: BASE, hasBody: true }, { body })
  }

  async cancel(
    subscriptionId: string,
    body: { immediate?: boolean; reason?: string }
  ): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${BASE}/{subscriptionId}/cancel`, hasBody: true },
      { subscriptionId, body }
    )
  }

  async reactivate(subscriptionId: string): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${BASE}/{subscriptionId}/reactivate`, hasBody: true },
      { subscriptionId, body: {} }
    )
  }

  async setDiscount(
    subscriptionId: string,
    body: { percent: number; endsAt?: string }
  ): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `${BASE}/{subscriptionId}/discount`, hasBody: true },
      { subscriptionId, body }
    )
  }

  async removeDiscount(subscriptionId: string): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: `${BASE}/{subscriptionId}/discount` },
      { subscriptionId }
    )
  }

  async addAddOn(subscriptionId: string, productId: string): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${BASE}/{subscriptionId}/add-ons`, hasBody: true },
      { subscriptionId, body: { productId } }
    )
  }

  async removeAddOn(
    subscriptionId: string,
    addOnSubscriptionId: string,
    immediate = false
  ): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: `${BASE}/{subscriptionId}/add-ons/{addOnSubscriptionId}`, hasBody: true },
      { subscriptionId, addOnSubscriptionId, body: { immediate } }
    )
  }

  async extend(
    subscriptionId: string,
    body: { days: number } | { until: string }
  ): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${BASE}/{subscriptionId}/extend`, hasBody: true },
      { subscriptionId, body }
    )
  }

  async sendPaymentLink(subscriptionId: string): Promise<AdminSubscriptionDetail> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${BASE}/{subscriptionId}/payment-link`, hasBody: true },
      { subscriptionId, body: {} }
    )
  }
}
